/**
 * Google Analytics event tracking utility
 * Uses gtag from @next/third-parties/google
 */

// Extend Window interface for TypeScript
declare global {
    interface Window {
        gtag?: (
            command: "config" | "event" | "js" | "set",
            targetId: string | Date,
            config?: Record<string, unknown>
        ) => void
    }
}

/**
 * Track a custom event in Google Analytics
 */
export function trackEvent(
    eventName: string,
    eventParams?: {
        action?: string
        category?: string
        label?: string
        value?: number
        [key: string]: unknown
    }
) {
    if (typeof window === "undefined" || !window.gtag) {
        // Silently fail in SSR or if gtag is not available
        return
    }

    window.gtag("event", eventName, {
        event_category: eventParams?.category || "general",
        event_label: eventParams?.label,
        value: eventParams?.value,
        ...eventParams,
    })
}

/**
 * Track theme toggle events
 */
export function trackThemeToggle(theme: "light" | "dark") {
    trackEvent("theme_toggle", {
        category: "ui_interaction",
        action: "toggle_theme",
        label: theme,
    })
}

/**
 * Track section visibility
 */
export function trackSectionView(sectionName: string) {
    trackEvent("section_view", {
        category: "engagement",
        action: "view_section",
        label: sectionName,
    })
}

/**
 * Track project interactions
 */
export function trackProjectClick(projectName: string) {
    trackEvent("project_click", {
        category: "project",
        action: "click",
        label: projectName,
    })
}

/**
 * Track external link clicks
 */
export function trackExternalLink(url: string, linkType: string) {
    trackEvent("external_link_click", {
        category: "outbound",
        action: "click",
        label: linkType,
        link_url: url,
    })
}

/**
 * Track CTA interactions
 */
export function trackCTAClick(ctaType: string) {
    trackEvent("cta_click", {
        category: "conversion",
        action: "click",
        label: ctaType,
    })
}

/**
 * Track project live site visits
 */
export function trackProjectVisit(projectName: string, url: string) {
    trackEvent("project_visit", {
        category: "project",
        action: "visit_live_site",
        label: projectName,
        link_url: url,
    })
}
